// Entry point. Factory-owned.
//
// Mounts the app and starts the chassis. Everything here runs once, in order,
// and the order matters: styles before mount so the first paint is not
// unstyled, mount before initPWA so a failed service-worker registration
// cannot stop the app rendering.

import { createApp } from 'vue';
import { IonicVue } from '@ionic/vue';

// Ionic's own stylesheets, then the palette, then the mapping, then yours.
// The order is the contract:
//
//   core/normalize/structure/typography  Ionic's reset and layout primitives
//   tokens.css                           the chassis scale: space, type, ink,
//                                        radii, motion, the 44px floor
//   ionic.css                            the brand ramp, the chrome, and the
//                                        rules that spend the scale
//   app.css                              yours
//   recipe.css                           the design recipe's overlay of the scale
//
// recipe.css is imported unconditionally and ships empty, so the load order is
// a fact of the chassis rather than something the design adapter has to edit
// into this file — which matters, because this file is factory-owned and an
// upgrade would take that edit straight back out. It carries nothing but
// `:root` custom properties, so any real rule in app.css still beats it.
//
// Ionic's own dark palette, following the OS. Respecting the platform's
// decision rather than deriving a parallel set of greys from the brand colour:
// the previous chassis hand-rolled eleven grey steps and a full dark ramp, and
// they were the reason a scaffolded app matched neither iOS nor Material.
import '@ionic/vue/css/core.css';
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';
// Ionic's optional utility classes — ion-padding, ion-text-center and friends.
// The pages use them instead of bespoke layout CSS, so they are not optional here.
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/palettes/dark.system.css';
import './styles/tokens.css';
import './styles/ionic.css';
import './styles/app.css';
import './styles/recipe.css';

import router, { HOME } from './router.js';
import App from './App.vue';
import { initPWA } from './core/pwa.js';
import { setState } from './core/state.js';
import { app } from './core/app.js';
import { externalizeSiteLinks, onAppUrlOpen } from './core/native.js';
import * as liveUpdate from './core/liveupdate.js';
import * as deeplink from './core/deeplink.js';
// Enabled addons, if any. An empty registry pulls in nothing, which is what
// makes an unused addon genuinely free rather than merely inert.
import addons from './addons.js';

// Ionic needs the plugin AND a router: `@ionic/vue` imports vue-router at
// module load, so there is no version of adopting Ionic that keeps a
// URL-less navigation scheme of its own.
const vueApp = createApp(App).use(IonicVue).use(router);

// WHICH RENDERING IS THIS? Recorded, because for a long time nothing said.
//
// Ionic resolves the mode ONCE at initialisation:
//
//   config.mode  ??  <html mode="">  ??  (isPlatform('ios') ? 'ios' : 'md')
//
// and `isPlatform('ios')` is a USER-AGENT test. So the shipped iOS binary gets
// `ios` and the shipped Android one gets `md`, both correct — and a desktop
// browser gets `md` whatever the app is. That includes `npm run dev` and the
// deployed `/app` and `/demo` surfaces, which is to say: every surface an agent
// or a person actually looks at while the app is being designed. An iOS-first
// app was therefore designed against Material by everyone who built it, and
// none of them could see it.
//
// The chassis does NOT pin the mode. Rendering as the platform is the whole
// reason this app is on Ionic, and pinning `ios` would put an iOS back chevron
// and iOS switches on an Android phone — the same tell in the other direction.
// The chrome in `styles/ionic.css` is written to be correct in both, and
// `AppLargeTitle` renders nothing off iOS so the DOM agrees with that.
//
// What was missing was the FACT. Ionic stamps the resolved mode as a class on
// <html>; this copies it onto a data attribute so a person can read it in
// devtools, QA can assert on it, and `?ionic:mode=ios` — the supported way to
// look at the iOS rendering in a desktop browser — is visibly taking effect.
// See `core/ui/house-style.md`.
const root = document.documentElement;
root.dataset.ionicMode = root.classList.contains('ios') ? 'ios' : 'md';

// A memory history starts at NO location at all, and `isReady()` waits for an
// initial navigation that will never happen on its own. Without this push the
// promise never settles, mount is never called, and the page stays blank with
// nothing in the console to explain it. Web history does this for you from the
// URL, which is why the trap only bites the mode that has no URL — and this
// app is a native shell and a PWA served from /app and /demo, so a URL-driven
// router would fight both the server's surface split and the deep-link handler
// below.
router.push(HOME);
router.isReady().then(() => {
  vueApp.mount('#app');
  // AND THE PAINTED PAD GOES. `index.html` draws the top of Today — the bar,
  // the jump bar and two ticket sheets — so the first frame is the app's own
  // shape rather than 3.5 s of blank document while this file's imports land.
  // It is removed HERE, in the same task as the mount, so the two happen in one
  // frame and there is never a moment with both on screen. It is never put
  // back: the router is in-memory, so a tab change does not reload the document
  // and cannot make it flash.
  document.getElementById('skeleton')?.remove();
});

// A way in, for QA only.
//
// This app navigates through a MEMORY history, so every URL the server serves
// renders the same initial screen. That is correct for a native shell and a
// PWA served at /app and /demo — and it silently defeated the screenshot pass:
// QA navigated to /tabs/items, got a 200, photographed Home, and reported that
// it had audited every screen. Five screens, five identical pictures, a green
// tick. Worse than no audit, because it reads as one.
//
// Gated on the flag the QA fixture sets before any app code runs, so nothing
// is exposed in a real build.
if (typeof window !== 'undefined' && window.__APPFACTORY_QA__) {
  window.__appfactoryQa = {
    navigate: (path) => router.push(path),
    route: () => router.currentRoute.value?.fullPath ?? null,

    // BY NAME as well, because a path is not stable and a name is.
    //
    // QA asks for a screen using the route the PLAN states — `/`, `/tasks/:id`.
    // The navigation shell decides where those actually mount, and bottom-tabs
    // nests all of them under /tabs/. So QA pushed `/`, the app arrived
    // correctly at `/tabs/`, and QA compared the two strings, saw a mismatch
    // and reported the primary screen as unreachable — on an app where
    // navigation worked perfectly.
    //
    // Every product route is registered with its plan id as `name`, so this is
    // the same question asked in a form no shell can rewrite.
    screen: (id) => router.push({ name: id }),
    id: () => {
      const n = router.currentRoute.value?.name;
      return typeof n === 'string' ? n : null;
    },
  };
}

setState({ ready: true });

// After mount, and deliberately not awaited. A service worker that fails to
// register, or an OTA check that cannot reach the server, must not leave the
// user looking at an empty screen — which is exactly what happened when the
// old chassis ran its boot sequence before rendering anything.
// BOTH UPDATE PATHS ASK THE SAME QUESTION.
//
// `AppHost.isIdle()` in core/app.js is documented as the one method you
// override to defer a reload while somebody is mid-edit, and `docs/OTA.md` said
// the chassis wired it to both the service worker and the OTA bundle. Neither
// call passed it. Both parameters had a `() => true` default and no caller, so
// overriding `isIdle` did nothing at all and every OTA payload reloaded the app
// the instant it finished downloading — including on resume, which is when
// somebody is most likely to be in the middle of something.
//
// Passed as a bound method rather than as a value: an app that overrides
// `isIdle` on the prototype has to be asked at the moment of the reload, not at
// boot.
const isSafeToReload = () => app.isIdle();

initPWA({ isSafeToReload });
externalizeSiteLinks();
liveUpdate.start({ isSafeToReload });

// Addons receive the host, exactly as they did on the 0.1 chassis — several of
// them extend it through `app.constructor.prototype`, and a plugin contract
// that changes with the renderer is not a contract.
for (const addon of addons) {
  // One addon that throws must not take the rest of them, or the app, with it.
  try { addon.boot?.(app); } catch (e) { console.error(`addon:${addon.id}`, e); }
}

// Deep links, from a cold start and from the native appUrlOpen event.
//
// `app.route()` emits on the bus; `deeplink.start()` is what LISTENS. Without
// it these two lines fired into nothing, and every link into the app — the
// marketing page's buttons, the operator's /app/admin, a shared link, every
// Universal Link the store lists — opened Home. Invisible until somebody builds
// a page per thing and gets six buttons with one destination.
//
// An app declares its own vocabulary with `defineLinks()`; the default is an
// empty rule set, which resolves everything to null and opens Home — the same
// behaviour as before, now by decision rather than by omission.
deeplink.start(router);
onAppUrlOpen((url) => app.route(url));
app.route(location.href);

// Boot finished without throwing.
//
// Set LAST, deliberately: it is the one signal that distinguishes "the page
// rendered" from "everything the chassis does actually ran". The smoke suite
// waits on it, and it is worth having outside the tests too — it is the
// difference between a blank screen you have to debug and a blank screen that
// tells you boot never completed.
document.documentElement.dataset.ready = '1';
